import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import Select from "react-select";
import APIs from "../../Constants/apis.js";
import swal from "sweetalert";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import PageSizeDropDownSelection, {
  SortOrderDropDownSelection,
} from "../../components/CustomComponents/DropdownSelections/DropDownSelections.jsx";
import { logout } from "../../app/auth/authSlice.js";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import { setVisibleColumns, setSortBy, setSortOrder } from "../../app/itemGridPreferencesSlice.js";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils.js";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import AddUpdateUnitModal from "./add&updateUnitModal.jsx";

const ManageUnits = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const pageKey = "units";
  const defaultVisibleColumns = {
    measurementTypeName: true,
    standardUnitName: true,
    symbol: true,
    measurement: true,
  };
  const defaultSortOrder = { value: "ASC", label: "Asc" };
  const defaultSortBy = { value: "MeasureName", label: "Unit Name" };
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;

  const [search, setSearch] = useState("");
  const [measurementTypeFilter, setMeasurementTypeFilter] = useState(null);
  const [pageSize, setPageSize] = useState({ value: "50", label: "50" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [unitToEdit, setUnitToEdit] = useState(null);

  const sortByOptions = [
    { value: "MeasureID", label: "ID" },
    { value: "MeasureName", label: "Unit Name" },
    { value: "Measurement", label: "Measurement Value" },
    { value: "StandardUnitName", label: "Standard Unit" },
    { value: "MeasurementTypeName", label: "Measurement Type" },
  ];

  const measurementTypeFilterOptions = [
    { value: null, label: "All Types" },
    { value: "Unit", label: "Unit" },
    { value: "Weight", label: "Weight" },
    { value: "Volume", label: "Volume" },
  ];

  const FetchUnits = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_UNITS, {
        params: {
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          Search: search.trim() || "",
          PageSize: pageSize.value,
          PageNumber: pageNumber,
          MeasurementTypeName: measurementTypeFilter?.value || null,
        },
      });

      if (response.data.status) {
        const unitData = response.data.data;

        if (Array.isArray(unitData)) {
          setUnits(unitData);
          setTotalRecords(response.data.totalRecords || 0);
          setTotalPages(response.data.totalPages || 1);
        } else {
          console.error("Unexpected API response format:", response.data);
          setUnits([]);
          setTotalRecords(0);
          setTotalPages(1);
        }
      } else {
        showErrorToast(response.data.Message || "Failed to fetch units");
        setUnits([]);
        setTotalRecords(0);
        setTotalPages(1);
      }

      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      if (err.response && err.response.status === 401) {
        dispatch(logout());
        setTimeout(() => {
          navigate("/user/login", { replace: true });
        }, 1000);
      } else {
        showErrorToast("Failed to fetch units");
      }
    }
  }, [search, sortBy, sortOrder, pageSize, pageNumber, measurementTypeFilter, dispatch, navigate]);

  useEffect(() => {
    FetchUnits();
  }, [FetchUnits]);

  // Initialize preferences on mount
  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    // eslint-disable-next-line
  }, [dispatch, pageKey]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPageNumber(1);
  };

  const handlePageSizeChange = (selectedOption) => {
    setPageSize(selectedOption);
    setPageNumber(1);
  };

  const handleSortByChange = (selectedOption) => {
    dispatch(setSortBy({ pageKey, sortBy: selectedOption }));
    setPageNumber(1);
  };

  const handleSortOrderChange = (selectedOption) => {
    dispatch(setSortOrder({ pageKey, sortOrder: selectedOption }));
    setPageNumber(1);
  };

  const handlePageChange = (page) => {
    setPageNumber(page);
  };

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      setSelectedUnits(units.map((unit) => unit.measureID));
    } else {
      setSelectedUnits([]);
    }
  };

  const handleSelectUnit = (unitId, e) => {
    const checked = e.target.checked;
    if (checked) {
      setSelectedUnits([...selectedUnits, unitId]);
    } else {
      setSelectedUnits(selectedUnits.filter((id) => id !== unitId));
    }
  };

  const handleDeleteUnit = async (unitId) => {
    try {
      const result = await swal({
        title: "Are you sure?",
        text: "Once deleted, you will not be able to recover this unit!",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (result) {
        setLoading(true);
        const response = await axiosInstance.delete(APIs.DELETE_UNIT, {
          params: {
            MeasureID: unitId,
          },
        });

        if (response.data.status) {
          showSuccessToast("Unit deleted successfully");
          FetchUnits();
        } else {
          showErrorToast(response.data.Message || "Failed to delete unit");
        }
        setLoading(false);
      }
    } catch (err) {
      console.log(err);
      setLoading(false);
      if (err.response && err.response.status === 401) {
        dispatch(logout());
        setTimeout(() => {
          navigate("/user/login", { replace: true });
        }, 1000);
      } else {
        showErrorToast("Failed to delete unit");
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUnits.length === 0) {
      showErrorToast("Please select units to delete");
      return;
    }

    try {
      const result = await swal({
        title: "Are you sure?",
        text: `This will delete ${selectedUnits.length} unit(s). Once deleted, you will not be able to recover them!`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (result) {
        setLoading(true);
        let successCount = 0;
        let failCount = 0;

        for (const unitId of selectedUnits) {
          try {
            const response = await axiosInstance.delete(APIs.DELETE_UNIT, {
              params: {
                MeasureID: unitId,
              },
            });

            if (response.data.status) {
              successCount++;
            } else {
              failCount++;
            }
          } catch (err) {
            failCount++;
          }
        }

        if (successCount > 0) {
          showSuccessToast(`${successCount} unit(s) deleted successfully`);
        }
        if (failCount > 0) {
          showErrorToast(`Failed to delete ${failCount} unit(s)`);
        }

        setSelectedUnits([]);
        setSelectAll(false);
        FetchUnits();
        setLoading(false);
      }
    } catch (err) {
      console.log(err);
      setLoading(false);
      showErrorToast("Failed to delete units");
    }
  };

  const handleEditUnit = (unit) => {
    setUnitToEdit(unit);
    setShowEditModal(true);
  };

  const handleUnitSubmit = async (unitData, isEdit, isBulkEdit) => {
    try {
      setLoading(true);
      let response;

      if (isEdit) {
        response = await axiosInstance.put(APIs.PUT_UPDATE_UNIT, {
          MeasureID: unitData.measureID,
          MeasureName: unitData.measureName.trim(),
          Measurement: unitData.measurement,
          StandardUnitName: unitData.standardUnitName,
          Symbol: unitData.symbol || null,
          MeasurementTypeName: unitData.measurementTypeName || null,
        });
      } else {
        response = await axiosInstance.post(APIs.POST_INSERT_UNIT, {
          MeasureName: unitData.measureName.trim(),
          Measurement: unitData.measurement,
          StandardUnitName: unitData.standardUnitName,
          Symbol: unitData.symbol || null,
          MeasurementTypeName: unitData.measurementTypeName || null,
        });
      }

      if (response.data.status) {
        showSuccessToast(
          isEdit ? "Unit updated successfully" : "Unit added successfully"
        );
        setShowAddModal(false);
        setShowEditModal(false);
        setUnitToEdit(null);
        FetchUnits();
      } else {
        showErrorToast(response.data.Message || "Failed to save unit");
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      if (err.response && err.response.status === 401) {
        dispatch(logout());
        setTimeout(() => {
          navigate("/user/login", { replace: true });
        }, 1000);
      } else {
        showErrorToast("Failed to save unit");
      }
    }
  };

  return (
    <div className="content">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">
                    <Link to="/dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">Manage Units</li>
                </ol>
                <h1 className="page-header mb-0">Manage Units</h1>
              </div>
              <div className="ms-auto">
                <button
                  className="btn px-4"
                  onClick={() => setShowAddModal(true)}
                  style={{
                    backgroundColor: themeColor,
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    border: "none",
                    color: "white",
                    borderRadius: "25px",
                    fontWeight: "500"
                  }}
                >
                  <i className="fa fa-plus fa-lg me-2 ms-n2"></i>
                  Add Unit
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                {/* Enhanced Filter Section */}
                <div className="mb-4">
                  <div
                    className="card filter-card"
                    style={{
                      position: "relative",
                      zIndex: 1,
                      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                      borderRadius: '12px',
                      border: 'none',
                      boxShadow: `0 4px 20px ${themeColor}40`
                    }}
                  >
                    <div className="card-body p-3">
                      {/* Filter Controls */}
                      <div className="row g-3 align-items-center">
                        {/* Search Field */}
                        <div className="col-xl-3 col-lg-3 col-md-6">
                          <div className="search-input filter-control">
                            <input
                              type="text"
                              className="filter-input"
                              value={search}
                              onChange={handleSearch}
                              placeholder="Search units..."
                              style={{
                                backgroundColor: isDarkMode ? 'rgba(45, 52, 54, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                                border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(255, 255, 255, 0.3)',
                                color: isDarkMode ? '#ffffff' : '#333',
                                borderRadius: '6px',
                                height: '36px'
                              }}
                            />
                          </div>
                        </div>

                        {/* Measurement Type Filter */}
                        <div className="col-xl-2 col-lg-2 col-md-6">
                          <div className="filter-control">
                            <Select
                              value={measurementTypeFilter}
                              onChange={(val) => {
                                setMeasurementTypeFilter(val);
                                setPageNumber(1);
                              }}
                              options={measurementTypeFilterOptions}
                              styles={isDarkMode ? DropDownStylesDark : DropDownStyles}
                              placeholder="Filter by type"
                              className="filter-dropdown"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              menuShouldBlockScroll={false}
                              menuShouldScrollIntoView={false}
                              closeMenuOnScroll={false}
                            />
                          </div>
                        </div>

                        {/* Sort By Dropdown */}
                        <div className="col-xl-2 col-lg-2 col-md-6">
                          <div className="filter-control">
                            <Select
                              value={sortBy}
                              onChange={handleSortByChange}
                              options={sortByOptions}
                              styles={isDarkMode ? DropDownStylesDark : DropDownStyles}
                              placeholder="Sort By"
                              className="filter-dropdown"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              menuShouldBlockScroll={false}
                              menuShouldScrollIntoView={false}
                              closeMenuOnScroll={false}
                            />
                          </div>
                        </div>

                        {/* Sort Order Dropdown */}
                        <div className="col-xl-2 col-lg-2 col-md-6">
                          <div className="filter-control">
                            <SortOrderDropDownSelection
                              defaultValue={sortOrder}
                              onChange={handleSortOrderChange}
                              styles={isDarkMode ? DropDownStylesDark : DropDownStyles}
                            />
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="col-xl-3 col-lg-3 col-md-12">
                          <div className="d-flex gap-2">
                            <button
                              className="clear-btn"
                              onClick={() => {
                                setSearch("");
                                setMeasurementTypeFilter(null);
                                setPageNumber(1);
                              }}
                              title="Clear all filters"
                              style={{
                                height: "32px",
                                backgroundColor: isDarkMode ? 'rgba(45, 52, 54, 0.8)' : 'rgba(255, 255, 255, 0.2)',
                                border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(255, 255, 255, 0.3)',
                                color: 'white',
                                borderRadius: '6px'
                              }}
                            >
                              <i className="fa fa-times me-1"></i>
                              Filter
                            </button>

                            <ColumnSelection
                              visibleColumns={visibleColumns}
                              setVisibleColumns={(cols) =>
                                dispatch(setVisibleColumns({ pageKey, columns: cols }))
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Selection Info and Actions (Shown only when units are selected) */}
                {selectedUnits.length > 0 && (
                  <div className="mb-3">
                    <div
                      className="p-3 rounded d-flex align-items-center justify-content-between"
                      style={{
                        backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                        border: isDarkMode ? "1px solid #404040" : "1px solid #dee2e6"
                      }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <span className="fw-bold" style={{ color: themeColor }}>
                          {selectedUnits.length} unit{selectedUnits.length > 1 ? 's' : ''} selected
                        </span>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => {
                            setSelectedUnits([]);
                            setSelectAll(false);
                          }}
                        >
                          Clear Selection
                        </button>
                      </div>

                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={handleBulkDelete}
                        >
                          <i className="fa fa-trash me-1"></i>Bulk Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div
                  className="table-responsive mt-3"
                  style={{
                    minHeight: "200px",
                    maxHeight: "450px",
                    overflowY: "auto",
                    overflowX: "auto",
                  }}
                >
                  <table className="table table-hover">
                    <thead
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 5,
                        backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }}
                    >
                      <tr>
                        <th className="pt-2 pb-2" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>
                          <input
                            type="checkbox"
                            onChange={handleSelectAll}
                            checked={
                              units.length > 0 &&
                              selectedUnits.length === units.length
                            }
                          />
                        </th>
                        <th className="pt-2 pb-2" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Unit Name</th>
                        {visibleColumns.measurementTypeName && (
                          <th className="pt-2 pb-2" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Type</th>
                        )}
                        {visibleColumns.standardUnitName && (
                          <th className="pt-2 pb-2" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Standard Unit</th>
                        )}
                        {visibleColumns.symbol && (
                          <th className="pt-2 pb-2" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Symbol</th>
                        )}
                        {visibleColumns.measurement && (
                          <th className="pt-2 pb-2 text-center" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Measurement</th>
                        )}
                        <th className="pt-2 pb-2 text-center" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="7" className="text-center">
                            <Preloader />
                          </td>
                        </tr>
                      ) : units.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center">
                            No units found
                          </td>
                        </tr>
                      ) : (
                        units.map((unit) => (
                          <tr key={unit.measureID}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedUnits.includes(unit.measureID)}
                                onChange={(e) => handleSelectUnit(unit.measureID, e)}
                              />
                            </td>
                            <td>{unit.measureName}</td>
                            {visibleColumns.measurementTypeName && (
                              <td>
                                <span className={`badge ${
                                  unit.measurementTypeName === "Weight" ? "bg-danger" :
                                  unit.measurementTypeName === "Volume" ? "bg-info" :
                                  "bg-secondary"
                                }`}>
                                  {unit.measurementTypeName || "-"}
                                </span>
                              </td>
                            )}
                            {visibleColumns.standardUnitName && (
                              <td>{unit.standardUnitName || "-"}</td>
                            )}
                            {visibleColumns.symbol && (
                              <td>{unit.symbol || "-"}</td>
                            )}
                            {visibleColumns.measurement && (
                              <td className="text-center">{unit.measurement}</td>
                            )}
                            <td className="text-center">
                              <div className="d-flex justify-content-center gap-2">
                                {/* Edit Button */}
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    border: `1px solid ${themeColor}`,
                                    color: themeColor,
                                    transition: 'all 0.2s ease',
                                    padding: 0
                                  }}
                                  onClick={() => handleEditUnit(unit)}
                                  title="Edit Unit"
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = themeColor;
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = themeColor;
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                >
                                  <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                                </button>

                                {/* Delete Button */}
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    border: '1px solid #dc3545',
                                    color: '#dc3545',
                                    transition: 'all 0.2s ease',
                                    padding: 0
                                  }}
                                  onClick={() => handleDeleteUnit(unit.measureID)}
                                  title="Delete Unit"
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#dc3545';
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = '#dc3545';
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                >
                                  <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="row mt-3">
                    <div className="col-12">
                      <Pagination
                        currentPage={pageNumber}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  </div>
                )}

                {/* Records Info */}
                <div className="row mt-3">
                  <div className="col-12">
                    <p style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                      Showing {units.length} of {totalRecords} units
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddUpdateUnitModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSubmit={handleUnitSubmit}
        isEdit={false}
        unit={null}
      />

      <AddUpdateUnitModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setUnitToEdit(null);
        }}
        onSubmit={handleUnitSubmit}
        isEdit={true}
        unit={unitToEdit}
      />

    </div>
  );
};

export default ManageUnits;

